import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
// import { globalApi } from "../../api/global";
import utils from "../../utils";
import { ROUTES } from "../../types/routes";
import { useI18n } from "../../i18n/hooks";
import { DownloadAppBarProps } from "./index.types";
import { Button } from "../Base/Button";
import { Icon } from "../Icon";
import { setGlobalValue } from "../../stores/GlobalState";
import { useUserDataFlattened } from "../../modules/user";
import {
  useEnableDownloadAppFloatingOnHomeTop,
  usePlatformLogo,
} from "../../modules/homeDisplay/hooks";
import "./DownloadAppBar.scss";
import { AppDownloadDialog } from "../AppDownloadDialog";

// import headerAppDownloadBarBackground from "@/assets/appDownload/headerAppDownloadBarBackground.png";
import { OptimizedImage } from "../Base";

// interface AppInfo {
//   apkLink?: string;
//   version?: string;
//   size?: string;
// }

export const DownloadAppBar: React.FC<DownloadAppBarProps> = ({
  className = "",
}) => {
  const { t } = useI18n();
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();
  const platformLogo = usePlatformLogo();
  const [topNavShow, setTopNavShow] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  // const [infoIos, setInfoIos] = useState<AppInfo | null>(null);
  const location = useLocation();
  const { isAuthenticated } = useUserDataFlattened();

  // 从 localstore 读取关闭状态，默认为 true（显示）
  const getInitialVisible = () => {
    try {
      const stored = utils.getObject("downloadAppBarVisible");
      return stored !== false; // 只有明确设置为 false 才隐藏，其他情况都显示
    } catch {
      return true;
    }
  };

  const [isVisible, setIsVisible] = useState(getInitialVisible);

  // 判断设备类型（仅用于下载时判断）
  // const getDeviceType = (): "Android" | "IOS" | "PC" => {
  //   const u = navigator.userAgent;
  //   const isAndroid = u.indexOf("Android") > -1 || u.indexOf("Adr") > -1;
  //   const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
  //   if (isAndroid) {
  //     return "Android";
  //   } else if (isIOS) {
  //     return "IOS";
  //   } else {
  //     return "PC";
  //   }
  // };

  // 获取iOS应用信息 - 只在需要时调用
  // const fetchIosInfo = async (): Promise<AppInfo | null> => {
  //   if (infoIos) return infoIos; // 如果已经有信息就返回现有信息
  //   try {
  //     const res = await globalApi.getIosAppInfo();
  //     if (res?.data?.model) {
  //       setInfoIos(res.data.model);
  //       return res.data.model; // 返回获取到的数据
  //     }
  //   } catch (error) {
  //     console.error("get ios app info failed:", error);
  //   }
  //   return null;
  // };

  // 同步最终的显示状态到全局状态
  useEffect(() => {
    setGlobalValue("downloadAppBarVisible", isVisible);
  }, [isVisible]);

  // 控制显示逻辑（需配置 enableDownloadAppFloatingOnHomeTop 为 true 才在首页顶部显示）
  useEffect(() => {
    // null 表示配置还在加载中，等待，不做任何副作用
    if (enableFloatingOnHomeTop === null) return;

    if (enableFloatingOnHomeTop === false) {
      setTopNavShow(false);
      setGlobalValue("downloadAppBarVisible", false);
      return;
    }

    const isHomePage =
      location.pathname === ROUTES.HOME ||
      location.pathname === ROUTES.HOME_ALT;

    // 如果不是首页，不显示
    if (!isHomePage) {
      setTopNavShow(false);
      return;
    }

    // 如果已安装APP（在APP内部），不显示，并且设置全局状态为 false
    if (utils.isApp() || utils.updateCheckIsPwa()) {
      setTopNavShow(false);
      setIsVisible(false);
      setGlobalValue("downloadAppBarVisible", false);
      return;
    }

    setTopNavShow(true);
  }, [location.pathname, isVisible, enableFloatingOnHomeTop]);

  const actualVisible = enableFloatingOnHomeTop && isVisible && topNavShow;
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("downloadAppBarChange", { detail: actualVisible })
    );
  }, [actualVisible]);

  // 关闭bar
  const handleClose = () => {
    setIsVisible(false);
    // 只有登录状态下关闭时才更新全局状态和持久化
    // 未登录时关闭不更新全局状态，保持Header的marginTop样式
    if (isAuthenticated) {
      setGlobalValue("downloadAppBarVisible", false);
      utils.setObject("downloadAppBarVisible", false);
    }
  };

  // 配置加载中，静默等待（组件保持在 React 树中，store 变化后能自动响应）
  if (enableFloatingOnHomeTop === null) {
    return null;
  }

  // 后台明确关闭顶部下载条时不显示
  if (enableFloatingOnHomeTop === false) {
    return null;
  }

  // 如果被用户关闭，不显示
  if (!isVisible) {
    return null;
  }

  // 如果不符合显示条件，不显示
  if (!topNavShow) {
    return null;
  }

  const noticeText = platformLogo?.appDownloadTitle;

  return (
    <>
      <div className={`downloadAppBar ${className}`}>
        {/* <img
        className="downloadAppBar__iconBackground"
        src={headerAppDownloadBarBackground}
        alt="Icon Background"
      /> */}
        <div className="downloadAppBar__inner">
          {/* 左边图标 */}
          <div className="downloadAppBar__iconWrapper">
            <OptimizedImage
              className="downloadAppBar__icon"
              src={utils.getImageUrl(window._config?.icon || "")}
              alt="App Icon"
              loading="eager"
              quality="medium"
            />
          </div>

          {/* 中间文本 */}
          {noticeText && (
            <div className="downloadAppBar__title">{noticeText}</div>
          )}

          {/* 右侧按钮组 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexShrink: 0,
            }}
          >
            {/* 下载按钮 */}
            <Button
              variant="secondary"
              className="downloadAppBar__button"
              onClick={() => setDialogVisible(true)}
              shape="rounded"
              size="sm"
            >
              {t("common.download")}
            </Button>

            {/* 关闭按钮 */}
            <button
              className="downloadAppBar__closeBtn"
              onClick={handleClose}
              aria-label="Close"
            >
              <Icon name="shareAppBarClose" />
            </button>
          </div>
        </div>
      </div>

      <AppDownloadDialog
        visible={dialogVisible}
        onClose={() => setDialogVisible(false)}
      />
    </>
  );
};

export default DownloadAppBar;
